import React, { useEffect, useState, useRef } from "react";
import {
  X,
  ArrowRight,
  RefreshCw,
  Calendar,
  CreditCard,
  ChevronDown,
  Sparkles,
  Loader2,
  Mail,
  Package,
  Settings,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import {
  revSyncService,
  RevSyncSettings,
  Pipeline,
  SettingsSuggestions,
  SettingsWithSuggestions,
  UpdateSettingsRequest,
} from "../../services/RevSyncService";
import { stripeIntegrationService } from "../../services/StripeIntegrationService";
import { hubSpotService } from "../../services/HubSpotService";
import { useToastContext } from "../../context/ToastContext";

interface RevSyncAgentSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  isClosing: boolean;
  isOpening: boolean;
}

const RevSyncAgentSettings: React.FC<RevSyncAgentSettingsProps> = ({
  isOpen,
  onClose,
  isClosing,
  isOpening,
}) => {
  const wizardRef = useRef<HTMLDivElement>(null);
  const [connections, setConnections] = useState<{
    hubspot: boolean;
    stripe: boolean;
    loading: boolean;
  }>({
    hubspot: false,
    stripe: false,
    loading: true,
  });

  const [activeTab, setActiveTab] = useState<"workflows" | "guide">("workflows");

  // Settings with suggestions state
  const [settings, setSettings] = useState<RevSyncSettings | null>(null);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [suggestions, setSuggestions] = useState<SettingsSuggestions | null>(null);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Product library status
  const [productLibraryStatus, setProductLibraryStatus] = useState<{
    enabled: boolean;
    autoEnabled?: boolean;
    productCount?: number;
    defaultProductName?: string;
    loading: boolean;
  }>({
    enabled: false,
    loading: false,
  });
  
  // Form state
  const [formData, setFormData] = useState<UpdateSettingsRequest>({
    autoCreateSubscriptions: false,
    netNewDealPipelineId: null,
    netNewDealStageId: null,
    enableRenewalDeals: false,
    renewalPipelineId: null,
    renewalStageId: null,
    // syncContractsToHubSpot is now managed via HubSpot API (contractSync state)
    cSuiteDailyReportEnabled: false,
  });
  
  // Renewal reminders state (separate from formData, uses HubSpot API)
  const [renewalReminders, setRenewalReminders] = useState<{
    enabled: boolean;
    daysBeforeExpiration: number;
    sourcePipelineId: string | null;
    pipelineId: string | null;
    dealStage: string | null;
    dealOwnerId: string | null;
  }>({
    enabled: false,
    daysBeforeExpiration: 30,
    sourcePipelineId: null,
    pipelineId: null,
    dealStage: null,
    dealOwnerId: null,
  });
  
  // Custom contract objects state (uses HubSpot API)
  const [contractSync, setContractSync] = useState<{
    enabled: boolean;
    setupStatus: "not_started" | "in_progress" | "success" | "failed";
    setupError: string | null;
    objectType: string | null;
    completedAt: string | null;
    enterpriseCapable: boolean | undefined;
  }>({
    enabled: false,
    setupStatus: "not_started",
    setupError: null,
    objectType: null,
    completedAt: null,
    enterpriseCapable: undefined,
  });
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Dropdown states for form fields
  const [isNetNewPipelineDropdownOpen, setIsNetNewPipelineDropdownOpen] = useState(false);
  const [isNetNewStageDropdownOpen, setIsNetNewStageDropdownOpen] = useState(false);
  const [isRenewalPipelineDropdownOpen, setIsRenewalPipelineDropdownOpen] = useState(false);
  const [isRenewalStageDropdownOpen, setIsRenewalStageDropdownOpen] = useState(false);
  const [isRenewalSourcePipelineDropdownOpen, setIsRenewalSourcePipelineDropdownOpen] = useState(false);
  const netNewPipelineDropdownRef = useRef<HTMLDivElement>(null);
  const netNewStageDropdownRef = useRef<HTMLDivElement>(null);
  const renewalPipelineDropdownRef = useRef<HTMLDivElement>(null);
  const renewalStageDropdownRef = useRef<HTMLDivElement>(null);
  const renewalSourcePipelineDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch HubSpot settings for renewal reminders
  const fetchHubSpotSettings = async () => {
    try {
      const { data } = await hubSpotService.getSettingsWithSuggestions(false);
      if (data.settings?.renewalReminders) {
        const rr = data.settings.renewalReminders;
        setRenewalReminders({
          enabled: rr.enabled,
          daysBeforeExpiration: rr.daysBeforeExpiration || 30,
          sourcePipelineId: rr.sourcePipelineId || null,
          pipelineId: rr.pipelineId || null,
          dealStage: rr.dealStage || null,
          dealOwnerId: rr.dealOwnerId || null,
        });
      }
    } catch (error: any) {
      console.error("Error fetching HubSpot settings:", error);
      // Don't show error toast - renewal reminders are optional
    }
  };

  // Fetch settings with suggestions when modal opens
  const fetchSettingsWithSuggestions = async (forceRefresh: boolean = false) => {
    setIsLoadingSettings(true);
    try {
      const [revSyncData, hubSpotData] = await Promise.all([
        revSyncService.getSettingsWithSuggestions(forceRefresh),
        hubSpotService.getSettingsWithSuggestions(forceRefresh),
      ]);
      
      const { data } = revSyncData;
      setSettings(data.settings);
      setPipelines(data.pipelines);
      setSuggestions(data.suggestions);
      setIsFirstTimeSetup(data.isFirstTimeSetup);

      // Update form data from settings, pre-filling with suggestions if available
      setFormData({
        autoCreateSubscriptions: data.settings.autoCreateSubscriptions,
        netNewDealPipelineId: data.settings.netNewDealPipelineId || data.suggestions?.netNewDeal?.pipelineId || null,
        netNewDealStageId: data.settings.netNewDealStageId || (data.suggestions?.netNewDeal?.pipelineId === (data.settings.netNewDealPipelineId || data.suggestions?.netNewDeal?.pipelineId) ? data.suggestions.netNewDeal?.stageId : null) || null,
        enableRenewalDeals: data.settings.enableRenewalDeals,
        renewalPipelineId: data.settings.renewalPipelineId || data.suggestions?.renewalDeal?.destinationPipeline?.pipelineId || null,
        renewalStageId: data.settings.renewalStageId || (data.suggestions?.renewalDeal?.destinationStage?.pipelineId === (data.settings.renewalPipelineId || data.suggestions?.renewalDeal?.destinationPipeline?.pipelineId) ? data.suggestions.renewalDeal.destinationStage?.stageId : null) || null,
        // Note: Contracts are automatically created when subscriptions are created
        // syncContractsToHubSpot is now managed via HubSpot API (contractSync state)
        cSuiteDailyReportEnabled: data.settings.cSuiteDailyReportEnabled,
      });

      // Update renewal reminders from HubSpot settings
      if (hubSpotData.data.settings?.renewalReminders) {
        const rr = hubSpotData.data.settings.renewalReminders;
        // Pre-fill with AI suggestions if available
        const renewalDaysSuggestion = (data.suggestions as any)?.renewalDays;
        const renewalReminderSuggestion = data.suggestions?.renewalDeal;
        
        setRenewalReminders({
          enabled: rr.enabled,
          daysBeforeExpiration: rr.daysBeforeExpiration || renewalDaysSuggestion?.value || 30,
          sourcePipelineId: rr.sourcePipelineId || renewalReminderSuggestion?.sourcePipeline?.pipelineId || null,
          pipelineId: rr.pipelineId || renewalReminderSuggestion?.destinationPipeline?.pipelineId || null,
          dealStage: rr.dealStage || renewalReminderSuggestion?.destinationStage?.stageId || null,
          dealOwnerId: rr.dealOwnerId || (data.suggestions as any)?.renewalDealOwner?.ownerId || null,
        });
      } else {
        // Initialize with suggestions if no settings exist
        const renewalDaysSuggestion = (data.suggestions as any)?.renewalDays;
        const renewalReminderSuggestion = data.suggestions?.renewalDeal;
        
        setRenewalReminders({
          enabled: false,
          daysBeforeExpiration: renewalDaysSuggestion?.value || 30,
          sourcePipelineId: renewalReminderSuggestion?.sourcePipeline?.pipelineId || null,
          pipelineId: renewalReminderSuggestion?.destinationPipeline?.pipelineId || null,
          dealStage: renewalReminderSuggestion?.destinationStage?.stageId || null,
          dealOwnerId: (data.suggestions as any)?.renewalDealOwner?.ownerId || null,
        });
      }

      // Update custom contract sync from HubSpot settings
      if (hubSpotData.data.settings) {
        const hs = hubSpotData.data.settings;
        setContractSync({
          enabled: hs.syncContractsToHubSpot || false,
          setupStatus: hs.customObjectSetupStatus || "not_started",
          setupError: hs.customObjectSetupError || null,
          objectType: hs.hubspotContractObjectType || null,
          completedAt: hs.customObjectSetupCompletedAt || null,
          enterpriseCapable: hs.hubspotEnterpriseCapable,
        });
      }

      // Show backend message if present
      if (data.userMessage) {
        showToast("info", data.userMessage);
      }
      
      // Fetch product library status if auto-create is enabled
      if (data.settings.autoCreateSubscriptions) {
        await fetchProductLibraryStatus();
      }
    } catch (error: any) {
      console.error("Error fetching settings with suggestions:", error);
      const errorMessage = error?.message || "Failed to load settings. Please try again.";
      showToast("error", errorMessage);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const { showToast } = useToastContext();

  useEffect(() => {
    const checkConnections = async () => {
      try {
        const [hubspotResult, stripeResult] = await Promise.allSettled([
          hubSpotService.getHubSpotStatus(),
          stripeIntegrationService.getStripeStatus(),
        ]);

        const hubspotStatus =
          hubspotResult.status === "fulfilled"
            ? hubspotResult.value.data?.connected ?? false
            : false;
        const stripeStatus =
          stripeResult.status === "fulfilled"
            ? stripeResult.value.data?.connected ?? false
            : false;

        setConnections({
          hubspot: hubspotStatus,
          stripe: stripeStatus,
          loading: false,
        });
      } catch (error) {
        console.error("Failed to fetch connection statuses:", error);
        setConnections({
          hubspot: false,
          stripe: false,
          loading: false,
        });
      }
    };

    if (isOpen) {
      checkConnections();
      // Fetch settings with suggestions when modal opens
      fetchSettingsWithSuggestions();
    }
  }, [isOpen]);

  const areWorkflowsEnabled = connections.hubspot && connections.stripe;

  // Validation helpers
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (formData.autoCreateSubscriptions) {
      if (!formData.netNewDealPipelineId || !formData.netNewDealStageId) {
        errors.netNewDeal = "Pipeline and Stage are required when auto-create is enabled";
      }
    }
    
    if (renewalReminders.enabled) {
      if (!renewalReminders.daysBeforeExpiration || renewalReminders.daysBeforeExpiration < 1 || renewalReminders.daysBeforeExpiration > 365) {
        errors.renewalDays = "Days before expiration must be between 1 and 365";
      }
      if (!renewalReminders.pipelineId) {
        errors.renewalPipeline = "Pipeline is required when renewal deals are enabled";
      }
      if (!renewalReminders.dealStage) {
        errors.renewalStage = "Stage is required when renewal deals are enabled";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const fetchProductLibraryStatus = async () => {
    if (!formData.autoCreateSubscriptions) {
      setProductLibraryStatus({ enabled: false, loading: false });
      return;
    }
    
    setProductLibraryStatus(prev => ({ ...prev, loading: true }));
    try {
      const { data } = await hubSpotService.getProductMappingRules();
      setProductLibraryStatus({
        enabled: data.enabled,
        autoEnabled: data.autoEnabled,
        productCount: data.productCount,
        defaultProductName: data.defaultProductName,
        loading: false,
      });
    } catch (error: any) {
      console.error("Error fetching product library status:", error);
      setProductLibraryStatus({ enabled: false, loading: false });
    }
  };

  const handleToggleAutoCreateSubscriptions = async (enabled: boolean) => {
    // If disabling, confirm first
    if (!enabled) {
      const confirmed = window.confirm(
        "Disable auto-subscription creation? Existing settings will be preserved."
      );
      if (!confirmed) {
        // User cancelled - toggle will remain ON (don't update formData)
        return;
      }
      // User confirmed - proceed with disabling
      setFormData(prev => ({ ...prev, autoCreateSubscriptions: false }));
      setProductLibraryStatus({ enabled: false, loading: false });
      await handleSaveSettingsForToggle(false);
      return;
    }

    // If enabling, validate pipeline/stage first
    if (!formData.netNewDealPipelineId || !formData.netNewDealStageId) {
      // Check if we have suggestions to auto-fill
      if (suggestions?.netNewDeal) {
        setFormData(prev => ({
          ...prev,
          autoCreateSubscriptions: true,
          netNewDealPipelineId: prev.netNewDealPipelineId || suggestions.netNewDeal!.pipelineId,
          netNewDealStageId: prev.netNewDealStageId || suggestions.netNewDeal!.stageId,
        }));
        // Save after auto-filling
        setTimeout(() => handleSaveSettingsForToggle(true), 100);
        return;
      } else {
        setValidationErrors(prev => ({
          ...prev,
          netNewDeal: "Please select a pipeline and stage first",
        }));
        return;
      }
    }

    // Update form data
    setFormData(prev => ({ ...prev, autoCreateSubscriptions: true }));
    
    // Save immediately
    await handleSaveSettingsForToggle(true);
  };

  const handleSaveSettingsForToggle = async (enabled: boolean) => {
    setIsSavingSettings(true);
    try {
      const response = await revSyncService.updateSettings({
        ...formData,
        autoCreateSubscriptions: enabled,
      });
      
      // Check for userMessage (product library auto-enabled)
      if (response.userMessage) {
        showToast("success", response.userMessage);
      } else if (enabled) {
        showToast("success", "✅ Ready! We'll create subscriptions automatically when deals are closed-won.");
      }
      
      // Fetch updated settings and product library status
      await Promise.all([
        fetchSettingsWithSuggestions(false),
        fetchProductLibraryStatus(),
      ]);
    } catch (error: any) {
      console.error("Error saving settings:", error);
      const errorMessage = error?.message || "Failed to save settings.";
      showToast("error", errorMessage);
      // Revert toggle on error
      setFormData(prev => ({ ...prev, autoCreateSubscriptions: !enabled }));
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleToggleRenewalReminders = async (enabled: boolean) => {
    // If disabling, confirm first
    if (!enabled) {
      const confirmed = window.confirm(
        "Disable renewal deals? Existing settings will be preserved."
      );
      if (!confirmed) {
        return;
      }
      setRenewalReminders(prev => ({ ...prev, enabled: false }));
      await handleSaveRenewalReminders(false);
      return;
    }

    // If enabling, validate required fields first
    if (!renewalReminders.daysBeforeExpiration || renewalReminders.daysBeforeExpiration < 1 || renewalReminders.daysBeforeExpiration > 365) {
      setValidationErrors(prev => ({
        ...prev,
        renewalDays: "Days before expiration must be between 1 and 365",
      }));
      return;
    }
    if (!renewalReminders.pipelineId || !renewalReminders.dealStage) {
      // Check if we have suggestions to auto-fill
      if (suggestions?.renewalDeal) {
        setRenewalReminders(prev => ({
          ...prev,
          enabled: true,
          pipelineId: prev.pipelineId || suggestions.renewalDeal!.destinationPipeline?.pipelineId || null,
          dealStage: prev.dealStage || suggestions.renewalDeal!.destinationStage?.stageId || null,
        }));
        // Save after auto-filling
        setTimeout(() => handleSaveRenewalReminders(true), 100);
        return;
      } else {
        setValidationErrors(prev => ({
          ...prev,
          renewalPipeline: "Pipeline and Stage are required to enable renewal deals",
        }));
        return;
      }
    }

    // Update state
    setRenewalReminders(prev => ({ ...prev, enabled: true }));
    
    // Save immediately
    await handleSaveRenewalReminders(true);
  };

  const handleSaveRenewalReminders = async (enabled: boolean) => {
    // Validate required fields when enabling
    if (enabled) {
      if (!renewalReminders.pipelineId || !renewalReminders.dealStage) {
        setValidationErrors(prev => ({
          ...prev,
          renewalPipeline: "Pipeline and Stage are required when renewal deals are enabled",
        }));
        setRenewalReminders(prev => ({ ...prev, enabled: false }));
        return;
      }
      if (!renewalReminders.daysBeforeExpiration || renewalReminders.daysBeforeExpiration < 1 || renewalReminders.daysBeforeExpiration > 365) {
        setValidationErrors(prev => ({
          ...prev,
          renewalDays: "Days before expiration must be between 1 and 365",
        }));
        setRenewalReminders(prev => ({ ...prev, enabled: false }));
        return;
      }
    }

    setIsSavingSettings(true);
    try {
      await hubSpotService.updateSettings({
        renewalReminders: {
          enabled,
          paused: false,
          daysBeforeExpiration: renewalReminders.daysBeforeExpiration || 30,
          sourcePipelineId: renewalReminders.sourcePipelineId || undefined,
          pipelineId: enabled && renewalReminders.pipelineId ? renewalReminders.pipelineId : (renewalReminders.pipelineId || ""),
          dealStage: enabled && renewalReminders.dealStage ? renewalReminders.dealStage : (renewalReminders.dealStage || ""),
          dealOwnerId: renewalReminders.dealOwnerId || undefined,
        },
      });
      
      if (enabled) {
        showToast("success", `✅ Renewal deals will be created automatically ${renewalReminders.daysBeforeExpiration} days before contracts expire.`);
      } else {
        showToast("success", "Renewal deals disabled.");
      }
      
      // Refresh settings
      await fetchSettingsWithSuggestions(false);
    } catch (error: any) {
      console.error("Error saving renewal reminders:", error);
      const errorMessage = error?.message || "Failed to save renewal deal settings.";
      showToast("error", errorMessage);
      // Revert toggle on error
      setRenewalReminders(prev => ({ ...prev, enabled: !enabled }));
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleToggleContractSync = async (enabled: boolean) => {
    if (!enabled) {
      // Disable sync (simple - no setup needed)
      setIsSavingSettings(true);
      try {
        await hubSpotService.updateSettings({
          syncContractsToHubSpot: false,
        });
        setContractSync(prev => ({ ...prev, enabled: false }));
        showToast("success", "Contract sync disabled.");
        await fetchSettingsWithSuggestions(false);
      } catch (error: any) {
        console.error("Error disabling contract sync:", error);
        showToast("error", error?.message || "Failed to disable contract sync.");
      } finally {
        setIsSavingSettings(false);
      }
      return;
    }

    // Enable sync - show loading, backend creates custom object
    setIsSavingSettings(true);
    try {
      const response = await hubSpotService.updateSettings({
        syncContractsToHubSpot: true,
      });
      
      // Status is in the response!
      const status = response.data.customObjectSetupStatus;
      const updatedSync = {
        enabled: response.data.syncContractsToHubSpot || false,
        setupStatus: status || "not_started",
        setupError: response.data.customObjectSetupError || null,
        objectType: response.data.hubspotContractObjectType || null,
        completedAt: response.data.customObjectSetupCompletedAt || null,
        enterpriseCapable: response.data.hubspotEnterpriseCapable,
      };
      setContractSync(updatedSync);

      if (status === "success") {
        showToast("success", "Custom object created successfully! Contracts will now sync to HubSpot.");
      } else if (status === "failed") {
        showToast("error", response.data.customObjectSetupError || "Setup failed. Please try again.");
        // Toggle will be reverted by backend
        setContractSync(prev => ({ ...prev, enabled: false }));
      }
      
      await fetchSettingsWithSuggestions(false);
    } catch (error: any) {
      console.error("Error enabling contract sync:", error);
      const errorMessage = error?.message || "Failed to enable contract sync.";
      if (errorMessage.includes("Enterprise")) {
        showToast("error", "HubSpot Enterprise is required to sync contracts. Please upgrade your HubSpot plan.");
      } else {
        showToast("error", errorMessage);
      }
      setContractSync(prev => ({ ...prev, enabled: false }));
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleRetryContractSetup = () => {
    handleToggleContractSync(true);
  };

  const handleSaveSettings = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSavingSettings(true);
    try {
      // Save RevSync settings
      const response = await revSyncService.updateSettings(formData);
      
      // Save renewal reminders to HubSpot (only if enabled and validated)
      if (renewalReminders.enabled) {
        if (!renewalReminders.pipelineId || !renewalReminders.dealStage) {
          throw new Error("Pipeline and Stage are required when renewal deals are enabled");
        }
        await hubSpotService.updateSettings({
          renewalReminders: {
            enabled: renewalReminders.enabled,
            paused: false,
            daysBeforeExpiration: renewalReminders.daysBeforeExpiration || 30,
            sourcePipelineId: renewalReminders.sourcePipelineId || undefined,
            pipelineId: renewalReminders.pipelineId,
            dealStage: renewalReminders.dealStage,
            dealOwnerId: renewalReminders.dealOwnerId || undefined,
          },
        });
      } else {
        // When disabled, preserve existing values
        await hubSpotService.updateSettings({
          renewalReminders: {
            enabled: false,
            paused: false,
            daysBeforeExpiration: renewalReminders.daysBeforeExpiration || 30,
            sourcePipelineId: renewalReminders.sourcePipelineId || undefined,
            pipelineId: renewalReminders.pipelineId || "",
            dealStage: renewalReminders.dealStage || "",
            dealOwnerId: renewalReminders.dealOwnerId || undefined,
          },
        });
      }
      
      // Check for userMessage
      if (response.userMessage) {
        showToast("success", response.userMessage);
      } else {
        showToast("success", response.message || "Settings saved successfully.");
      }
      
      await Promise.all([
        fetchSettingsWithSuggestions(false),
        fetchProductLibraryStatus(),
      ]);
    } catch (error: any) {
      console.error("Error saving settings:", error);
      const errorMessage = error?.message || "Failed to save settings.";
      showToast("error", errorMessage);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Helper functions for form
  const getStageName = (stageId: string | null, pipelineId: string | null) => {
    if (!stageId || !pipelineId) return null;
    const pipeline = pipelines.find((p) => p.id === pipelineId);
    if (!pipeline) return null;
    const stage = pipeline.stages.find((s) => s.id === stageId);
    return stage ? stage.label : null;
  };

  const getNetNewPipeline = () => {
    if (!formData.netNewDealPipelineId) return null;
    return pipelines.find((p) => p.id === formData.netNewDealPipelineId);
  };

  const getNetNewStages = () => {
    const pipeline = getNetNewPipeline();
    return pipeline ? pipeline.stages : [];
  };

  const getRenewalPipeline = () => {
    if (!renewalReminders.pipelineId) return null;
    return pipelines.find((p) => p.id === renewalReminders.pipelineId);
  };

  const getRenewalStages = () => {
    const pipeline = getRenewalPipeline();
    return pipeline ? pipeline.stages : [];
  };

  const handleGetAISuggestions = async () => {
    await fetchSettingsWithSuggestions(true);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        netNewPipelineDropdownRef.current &&
        !netNewPipelineDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNetNewPipelineDropdownOpen(false);
      }
      if (
        netNewStageDropdownRef.current &&
        !netNewStageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNetNewStageDropdownOpen(false);
      }
      if (
        renewalSourcePipelineDropdownRef.current &&
        !renewalSourcePipelineDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRenewalSourcePipelineDropdownOpen(false);
      }
      if (
        renewalPipelineDropdownRef.current &&
        !renewalPipelineDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRenewalPipelineDropdownOpen(false);
      }
      if (
        renewalStageDropdownRef.current &&
        !renewalStageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRenewalStageDropdownOpen(false);
      }
    };

    if (
      isNetNewPipelineDropdownOpen ||
      isNetNewStageDropdownOpen ||
      isRenewalSourcePipelineDropdownOpen ||
      isRenewalPipelineDropdownOpen ||
      isRenewalStageDropdownOpen
    ) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    isNetNewPipelineDropdownOpen,
    isNetNewStageDropdownOpen,
    isRenewalSourcePipelineDropdownOpen,
    isRenewalPipelineDropdownOpen,
    isRenewalStageDropdownOpen,
  ]);


  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
        style={{
          opacity: isClosing ? 0 : isOpening ? 0 : 1,
          pointerEvents: isClosing ? "none" : "auto",
          transition: isClosing
            ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
        onClick={onClose}
      />

      {/* Settings Panel */}
      <div
        ref={wizardRef}
        className="fixed inset-y-0 right-0 w-full max-w-2xl bg-background-paper dark:bg-background-paperDark shadow-2xl z-50 overflow-y-auto"
        style={{
          transform: isOpening
            ? "translateX(100%)"
            : isClosing
            ? "translateX(100%)"
            : "translateX(0)",
          opacity: isClosing ? 0 : 1,
          transition: isClosing
            ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-divider dark:border-divider-dark">
            <div>
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                RevSync Agent Settings
              </h2>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-1">
                Configure automation workflows and preferences
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 relative">
            {/* Loading Overlay - Agent Screen */}
            {isLoadingSettings && (
              <div className="absolute inset-0 bg-background-paper dark:bg-background-paperDark z-50 flex items-center justify-center">
                <div className="text-center max-w-xs px-6">
                  {/* Minimal pulsing indicator */}
                  <div className="relative mb-8">
                    <div className="w-2.5 h-2.5 mx-auto rounded-full bg-secondary animate-pulse" style={{ animationDuration: "0.8s" }}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-secondary/30 animate-ping" style={{ animationDuration: "0.8s" }}></div>
                    </div>
                  </div>

                  {/* Title - Minimal with RevSync */}
                  <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                    <span className="font-semibold text-[#F57E0C]">RevSync</span> is analyzing your setup
                  </p>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className={`flex items-center gap-1 mb-6 border-b border-divider dark:border-divider-dark transition-opacity ${isLoadingSettings ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
              <button
                onClick={() => setActiveTab("workflows")}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  activeTab === "workflows"
                    ? "text-text-primary dark:text-text-primaryDark"
                    : "text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark"
                }`}
              >
                Automation Workflows
                {activeTab === "workflows" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary" />
                )}
              </button>
              <button
                onClick={() => setActiveTab("guide")}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  activeTab === "guide"
                    ? "text-text-primary dark:text-text-primaryDark"
                    : "text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark"
                }`}
              >
                Setup Guide
                {activeTab === "guide" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary" />
                )}
              </button>
            </div>

            {/* Automation Workflows Tab */}
            {/* Automation Workflows Tab */}
            {activeTab === "workflows" && (
              <div className={isLoadingSettings ? "opacity-0 pointer-events-none" : "opacity-100 transition-opacity duration-300"}>
                {/* AI Suggestions Badge */}
                {isFirstTimeSetup && suggestions && (
                  <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-blue-900 dark:text-blue-300">
                        AI Suggestions Applied
                      </span>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      We've analyzed your HubSpot setup and suggested the best configuration. Review and adjust as needed.
                    </p>
                    {suggestions.netNewDeal && (
                      <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                        <strong>Confidence:</strong> {suggestions.netNewDeal.confidence}
                        {suggestions.netNewDeal.reasoning && (
                          <span className="ml-2">• {suggestions.netNewDeal.reasoning}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!isLoadingSettings && (
                  <form onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }} className="space-y-6">
                    {/* Auto-Create Stripe Subscriptions Section */}
                    <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                            Auto-Create Stripe Subscriptions
                          </h3>
                          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                            Automatically create subscriptions in Stripe when deals reach the specified stage
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleAutoCreateSubscriptions(!formData.autoCreateSubscriptions)}
                          disabled={isSavingSettings}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                            formData.autoCreateSubscriptions
                              ? "bg-secondary"
                              : "bg-black/20 dark:bg-white/20"
                          } ${isSavingSettings ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.autoCreateSubscriptions
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>

                      <div className={`grid grid-cols-2 gap-4 transition-all duration-200 ${
                        formData.autoCreateSubscriptions ? "opacity-100" : "opacity-50"
                      }`}>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                            Pipeline
                            {suggestions?.netNewDeal && formData.netNewDealPipelineId === suggestions.netNewDeal.pipelineId && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                AI Suggested
                              </span>
                            )}
                          </label>
                          <div className="relative" ref={netNewPipelineDropdownRef}>
                            <button
                              type="button"
                              onClick={() => {
                                setIsNetNewPipelineDropdownOpen(!isNetNewPipelineDropdownOpen);
                                setIsNetNewStageDropdownOpen(false);
                              }}
                              disabled={!formData.autoCreateSubscriptions}
                              className="w-full flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark hover:border-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span>
                                {formData.netNewDealPipelineId
                                  ? pipelines.find((p) => p.id === formData.netNewDealPipelineId)?.label
                                  : "Select pipeline"}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 text-text-secondary dark:text-text-secondaryDark transition-transform ${
                                  isNetNewPipelineDropdownOpen ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {isNetNewPipelineDropdownOpen && (
                              <div className="absolute z-10 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                                {pipelines.map((pipeline) => (
                                  <button
                                    key={pipeline.id}
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, netNewDealPipelineId: pipeline.id, netNewDealStageId: null }));
                                      setIsNetNewPipelineDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors ${
                                      formData.netNewDealPipelineId === pipeline.id
                                        ? "bg-background-panel dark:bg-background-panelDark text-secondary"
                                        : "text-text-primary dark:text-text-primaryDark"
                                    }`}
                                  >
                                    {pipeline.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                            Stage
                            {suggestions?.netNewDeal && formData.netNewDealPipelineId === suggestions.netNewDeal.pipelineId && formData.netNewDealStageId === suggestions.netNewDeal.stageId && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                AI Suggested
                              </span>
                            )}
                          </label>
                          <div className="relative" ref={netNewStageDropdownRef}>
                            <button
                              type="button"
                              onClick={() => {
                                setIsNetNewStageDropdownOpen(!isNetNewStageDropdownOpen);
                                setIsNetNewPipelineDropdownOpen(false);
                              }}
                              disabled={!formData.autoCreateSubscriptions || !formData.netNewDealPipelineId}
                              className="w-full flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark hover:border-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span>
                                {formData.netNewDealStageId
                                  ? getStageName(formData.netNewDealStageId, formData.netNewDealPipelineId)
                                  : "Select stage"}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 text-text-secondary dark:text-text-secondaryDark transition-transform ${
                                  isNetNewStageDropdownOpen ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {isNetNewStageDropdownOpen && (
                              <div className="absolute z-10 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                                {getNetNewStages().map((stage) => (
                                  <button
                                    key={stage.id}
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, netNewDealStageId: stage.id }));
                                      setIsNetNewStageDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors ${
                                      formData.netNewDealStageId === stage.id
                                        ? "bg-background-panel dark:bg-background-panelDark text-secondary"
                                        : "text-text-primary dark:text-text-primaryDark"
                                    }`}
                                  >
                                    {stage.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {validationErrors.netNewDeal && (
                        <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                          {validationErrors.netNewDeal}
                        </div>
                      )}

                      {/* Product Library Status Card */}
                      {formData.autoCreateSubscriptions && (
                        <div className="mt-4 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark p-4">
                          <div className="flex items-start gap-3">
                            <Package className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                                Product Library Status
                              </h4>
                              {productLibraryStatus.loading ? (
                                <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Checking products...</span>
                                </div>
                              ) : productLibraryStatus.enabled ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-sm">
                                    {productLibraryStatus.autoEnabled ? (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="text-text-primary dark:text-text-primaryDark">
                                          Auto-enabled
                                          {productLibraryStatus.productCount !== undefined && ` (${productLibraryStatus.productCount} products)`}
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="text-text-primary dark:text-text-primaryDark">
                                          Enabled
                                          {productLibraryStatus.productCount !== undefined && ` (${productLibraryStatus.productCount} products)`}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {productLibraryStatus.defaultProductName && (
                                    <div className="text-xs text-text-secondary dark:text-text-secondaryDark ml-6">
                                      Default: {productLibraryStatus.defaultProductName}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                                  {productLibraryStatus.productCount === 0 ? (
                                    <>
                                      <Package className="w-4 h-4" />
                                      <span>No products found - Using auto-create from deals</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4" />
                                      <span>Disabled - Using auto-create from deals</span>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Field Mappings Status Card */}
                      {formData.autoCreateSubscriptions && suggestions?.subscriptionSetup?.fieldMappings && (
                        <div className="mt-4 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark p-4">
                          <div className="flex items-start gap-3">
                            <Settings className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                                Field Mappings
                              </h4>
                              <div className="flex items-center gap-2 mb-3">
                                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="text-sm text-text-primary dark:text-text-primaryDark">Configured</span>
                              </div>
                              <div className="space-y-1.5 text-xs">
                                {suggestions.subscriptionSetup.fieldMappings.amount && (
                                  <div className="text-text-secondary dark:text-text-secondaryDark">
                                    <span className="font-medium">Amount:</span> {suggestions.subscriptionSetup.fieldMappings.amount.field}
                                    {suggestions.subscriptionSetup.fieldMappings.amount.confidence && (
                                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                        suggestions.subscriptionSetup.fieldMappings.amount.confidence === "high"
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                          : suggestions.subscriptionSetup.fieldMappings.amount.confidence === "medium"
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                      }`}>
                                        {suggestions.subscriptionSetup.fieldMappings.amount.confidence} confidence
                                      </span>
                                    )}
                                  </div>
                                )}
                                {suggestions.subscriptionSetup.fieldMappings.billingCycle && (
                                  <div className="text-text-secondary dark:text-text-secondaryDark">
                                    <span className="font-medium">Billing Cycle:</span> {suggestions.subscriptionSetup.fieldMappings.billingCycle.field}
                                    {suggestions.subscriptionSetup.fieldMappings.billingCycle.confidence && (
                                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                        suggestions.subscriptionSetup.fieldMappings.billingCycle.confidence === "high"
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                          : suggestions.subscriptionSetup.fieldMappings.billingCycle.confidence === "medium"
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                      }`}>
                                        {suggestions.subscriptionSetup.fieldMappings.billingCycle.confidence} confidence
                                      </span>
                                    )}
                                  </div>
                                )}
                                {suggestions.subscriptionSetup.fieldMappings.startDate && (
                                  <div className="text-text-secondary dark:text-text-secondaryDark">
                                    <span className="font-medium">Start Date:</span> {suggestions.subscriptionSetup.fieldMappings.startDate.field}
                                    {suggestions.subscriptionSetup.fieldMappings.startDate.confidence && (
                                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                        suggestions.subscriptionSetup.fieldMappings.startDate.confidence === "high"
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                          : suggestions.subscriptionSetup.fieldMappings.startDate.confidence === "medium"
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                      }`}>
                                        {suggestions.subscriptionSetup.fieldMappings.startDate.confidence} confidence
                                      </span>
                                    )}
                                  </div>
                                )}
                                {suggestions.subscriptionSetup.fieldMappings.currency && (
                                  <div className="text-text-secondary dark:text-text-secondaryDark">
                                    <span className="font-medium">Currency:</span> {suggestions.subscriptionSetup.fieldMappings.currency.field}
                                    {suggestions.subscriptionSetup.fieldMappings.currency.confidence && (
                                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                        suggestions.subscriptionSetup.fieldMappings.currency.confidence === "high"
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                          : suggestions.subscriptionSetup.fieldMappings.currency.confidence === "medium"
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                      }`}>
                                        {suggestions.subscriptionSetup.fieldMappings.currency.confidence} confidence
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Create Renewal Deals in HubSpot Section */}
                    <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                            Create Renewal Deals in HubSpot
                          </h3>
                          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                            Automatically create renewal deals in HubSpot based on deal end dates
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleRenewalReminders(!renewalReminders.enabled)}
                          disabled={isSavingSettings}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                            renewalReminders.enabled
                              ? "bg-secondary"
                              : "bg-black/20 dark:bg-white/20"
                          } ${isSavingSettings ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              renewalReminders.enabled
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>

                      <div className={`space-y-4 transition-all duration-200 ${
                        renewalReminders.enabled ? "opacity-100" : "opacity-50"
                      }`}>
                        {/* Days Before Expiration */}
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                            Days Before Expiration
                            {(suggestions as any)?.renewalDays && renewalReminders.daysBeforeExpiration === (suggestions as any).renewalDays.value && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                AI Suggested
                              </span>
                            )}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={renewalReminders.daysBeforeExpiration || 30}
                              onChange={(e) => {
                                const value = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                                if (e.target.value === "" || (!isNaN(value) && value >= 1 && value <= 365)) {
                                  setRenewalReminders(prev => ({ 
                                    ...prev, 
                                    daysBeforeExpiration: e.target.value === "" ? 0 : value 
                                  }));
                                  if (value >= 1 && value <= 365) {
                                    setValidationErrors(prev => ({ ...prev, renewalDays: "" }));
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (isNaN(value) || value < 1 || value > 365) {
                                  setRenewalReminders(prev => ({ ...prev, daysBeforeExpiration: 30 }));
                                  setValidationErrors(prev => ({ ...prev, renewalDays: "" }));
                                }
                              }}
                              disabled={!renewalReminders.enabled}
                              className="w-24 px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm text-text-secondary dark:text-text-secondaryDark">days</span>
                            {(suggestions as any)?.renewalDays && (
                              <div className="flex items-center gap-1 text-xs text-text-secondary dark:text-text-secondaryDark">
                                <span>•</span>
                                <span>{(suggestions as any).renewalDays.reasoning}</span>
                              </div>
                            )}
                          </div>
                          {validationErrors.renewalDays && (
                            <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                              {validationErrors.renewalDays}
                            </div>
                          )}
                        </div>

                        {/* Source Pipeline (Optional) */}
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                            Source Pipeline (Optional)
                            {suggestions?.renewalDeal?.sourcePipeline && renewalReminders.sourcePipelineId === suggestions.renewalDeal.sourcePipeline.pipelineId && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                AI Suggested
                              </span>
                            )}
                          </label>
                          <p className="text-xs text-text-secondary dark:text-text-secondaryDark mb-2">
                            Which pipeline to listen to for contracts. Defaults to subscription creation pipeline.
                          </p>
                          <div className="relative" ref={renewalSourcePipelineDropdownRef}>
                            <button
                              type="button"
                              onClick={() => {
                                setIsRenewalSourcePipelineDropdownOpen(!isRenewalSourcePipelineDropdownOpen);
                                setIsRenewalPipelineDropdownOpen(false);
                                setIsRenewalStageDropdownOpen(false);
                              }}
                              disabled={!renewalReminders.enabled}
                              className="w-full flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark hover:border-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span>
                                {renewalReminders.sourcePipelineId
                                  ? pipelines.find((p) => p.id === renewalReminders.sourcePipelineId)?.label
                                  : "Select pipeline (optional)"}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 text-text-secondary dark:text-text-secondaryDark transition-transform ${
                                  isRenewalSourcePipelineDropdownOpen ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {isRenewalSourcePipelineDropdownOpen && (
                              <div className="absolute z-10 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRenewalReminders(prev => ({ ...prev, sourcePipelineId: null }));
                                    setIsRenewalSourcePipelineDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors text-text-primary dark:text-text-primaryDark"
                                >
                                  None (use default)
                                </button>
                                {pipelines.map((pipeline) => (
                                  <button
                                    key={pipeline.id}
                                    type="button"
                                    onClick={() => {
                                      setRenewalReminders(prev => ({ ...prev, sourcePipelineId: pipeline.id }));
                                      setIsRenewalSourcePipelineDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors ${
                                      renewalReminders.sourcePipelineId === pipeline.id
                                        ? "bg-background-panel dark:bg-background-panelDark text-secondary"
                                        : "text-text-primary dark:text-text-primaryDark"
                                    }`}
                                  >
                                    {pipeline.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Destination Pipeline and Stage */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                              Pipeline
                              {suggestions?.renewalDeal?.destinationPipeline && renewalReminders.pipelineId === suggestions.renewalDeal.destinationPipeline.pipelineId && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                  AI Suggested
                                </span>
                              )}
                            </label>
                            <div className="relative" ref={renewalPipelineDropdownRef}>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsRenewalPipelineDropdownOpen(!isRenewalPipelineDropdownOpen);
                                  setIsRenewalStageDropdownOpen(false);
                                  setIsRenewalSourcePipelineDropdownOpen(false);
                                }}
                                disabled={!renewalReminders.enabled}
                                className="w-full flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark hover:border-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <span>
                                  {renewalReminders.pipelineId
                                    ? pipelines.find((p) => p.id === renewalReminders.pipelineId)?.label
                                    : "Select pipeline"}
                                </span>
                                <ChevronDown
                                  className={`w-4 h-4 text-text-secondary dark:text-text-secondaryDark transition-transform ${
                                    isRenewalPipelineDropdownOpen ? "rotate-180" : ""
                                  }`}
                                />
                              </button>
                              {isRenewalPipelineDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                                  {pipelines.map((pipeline) => (
                                    <button
                                      key={pipeline.id}
                                      type="button"
                                      onClick={() => {
                                        setRenewalReminders(prev => ({ ...prev, pipelineId: pipeline.id, dealStage: null }));
                                        setIsRenewalPipelineDropdownOpen(false);
                                      }}
                                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors ${
                                        renewalReminders.pipelineId === pipeline.id
                                          ? "bg-background-panel dark:bg-background-panelDark text-secondary"
                                          : "text-text-primary dark:text-text-primaryDark"
                                      }`}
                                    >
                                      {pipeline.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                              Stage
                              {suggestions?.renewalDeal?.destinationStage && renewalReminders.pipelineId === suggestions.renewalDeal.destinationStage.pipelineId && renewalReminders.dealStage === suggestions.renewalDeal.destinationStage.stageId && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                  AI Suggested
                                </span>
                              )}
                            </label>
                            <div className="relative" ref={renewalStageDropdownRef}>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsRenewalStageDropdownOpen(!isRenewalStageDropdownOpen);
                                  setIsRenewalPipelineDropdownOpen(false);
                                  setIsRenewalSourcePipelineDropdownOpen(false);
                                }}
                                disabled={!renewalReminders.enabled || !renewalReminders.pipelineId}
                                className="w-full flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark hover:border-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <span>
                                  {renewalReminders.dealStage
                                    ? getStageName(renewalReminders.dealStage, renewalReminders.pipelineId)
                                    : "Select stage"}
                                </span>
                                <ChevronDown
                                  className={`w-4 h-4 text-text-secondary dark:text-text-secondaryDark transition-transform ${
                                    isRenewalStageDropdownOpen ? "rotate-180" : ""
                                  }`}
                                />
                              </button>
                              {isRenewalStageDropdownOpen && renewalReminders.pipelineId && (
                                <div className="absolute z-10 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                                  {(() => {
                                    const pipeline = pipelines.find((p) => p.id === renewalReminders.pipelineId);
                                    return pipeline ? pipeline.stages.map((stage) => (
                                      <button
                                        key={stage.id}
                                        type="button"
                                        onClick={() => {
                                          setRenewalReminders(prev => ({ ...prev, dealStage: stage.id }));
                                          setIsRenewalStageDropdownOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors ${
                                          renewalReminders.dealStage === stage.id
                                            ? "bg-background-panel dark:bg-background-panelDark text-secondary"
                                            : "text-text-primary dark:text-text-primaryDark"
                                        }`}
                                      >
                                        {stage.label}
                                      </button>
                                    )) : [];
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {(validationErrors.renewalPipeline || validationErrors.renewalStage) && (
                          <div className="text-sm text-red-600 dark:text-red-400">
                            {validationErrors.renewalPipeline || validationErrors.renewalStage}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Create Contract Objects in HubSpot Section */}
                    <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                            Create Contract Objects in HubSpot
                          </h3>
                          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                            Automatically create custom contract objects in HubSpot that mirror your platform contracts (1-to-1 mapping)
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleContractSync(!contractSync.enabled)}
                          disabled={!contractSync.enterpriseCapable || isSavingSettings}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                            contractSync.enabled
                              ? "bg-secondary"
                              : "bg-black/20 dark:bg-white/20"
                          } ${!contractSync.enterpriseCapable || isSavingSettings ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          {isSavingSettings && !contractSync.enabled ? (
                            <Loader2 className="w-4 h-4 text-white animate-spin absolute left-1" />
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                contractSync.enabled
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          )}
                        </button>
                      </div>

                      {/* Enterprise Requirement Notice */}
                      {contractSync.enterpriseCapable === false && (
                        <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                                Requires HubSpot Enterprise
                              </p>
                              <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-2">
                                Your account does not have Enterprise access
                              </p>
                              <a
                                href="https://www.hubspot.com/pricing"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-yellow-800 dark:text-yellow-300 underline hover:text-yellow-900 dark:hover:text-yellow-200"
                              >
                                Upgrade HubSpot →
                              </a>
                            </div>
                          </div>
                        </div>
                      )}

                      {contractSync.enterpriseCapable === true && (
                        <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
                                Requires HubSpot Enterprise
                              </p>
                              <p className="text-xs text-blue-700 dark:text-blue-400">
                                ✅ Your account has Enterprise access
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Setup Status Card */}
                      {contractSync.enabled && (
                        <div className="mt-4 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark p-4">
                          {contractSync.setupStatus === "success" && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                                <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                                  Active
                                </span>
                              </div>
                              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                Custom object created successfully. Contracts are syncing to HubSpot.
                              </p>
                              {contractSync.objectType && (
                                <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                  <span className="font-medium">Object Type:</span> {contractSync.objectType}
                                </div>
                              )}
                              {contractSync.completedAt && (
                                <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                  <span className="font-medium">Completed:</span> {new Date(contractSync.completedAt).toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}

                          {contractSync.setupStatus === "failed" && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                                  Setup Failed
                                </span>
                              </div>
                              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                {contractSync.setupError || "Setup failed. Please try again."}
                              </p>
                              <button
                                type="button"
                                onClick={handleRetryContractSetup}
                                disabled={isSavingSettings}
                                className="px-4 py-2 text-sm font-medium text-white bg-secondary rounded-lg hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isSavingSettings ? (
                                  <span className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Retrying...
                                  </span>
                                ) : (
                                  "Retry Setup"
                                )}
                              </button>
                            </div>
                          )}

                          {contractSync.setupStatus === "in_progress" && (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                                  In Progress
                                </p>
                                <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                  Setting up custom object in HubSpot... This may take 5-10 seconds.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* C-Suite Daily Report Section */}
                    <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                            <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
                              C-Suite Daily Report
                            </h3>
                          </div>
                          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                            Receive a daily morning email snapshot with total ARR, RevSync activity, and key revenue metrics
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, cSuiteDailyReportEnabled: !prev.cSuiteDailyReportEnabled }))}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                            formData.cSuiteDailyReportEnabled
                              ? "bg-secondary"
                              : "bg-black/20 dark:bg-white/20"
                          } cursor-pointer`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.cSuiteDailyReportEnabled
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-4 border-t border-divider dark:border-divider-dark">
                      <button
                        type="button"
                        onClick={handleGetAISuggestions}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Get AI Suggestions
                      </button>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-4 py-2 text-sm font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSavingSettings}
                          className="px-6 py-2 text-sm font-medium text-white bg-secondary hover:bg-secondary-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isSavingSettings && <Loader2 className="w-4 h-4 animate-spin" />}
                          Save Settings
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            )}


            {/* Setup Guide Tab */}
            {activeTab === "guide" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                    HubSpot Workflow Setup Guide
                  </h3>
                  <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
                    Learn how to configure and use each automation workflow
                  </p>
                </div>

                {/* Getting Started */}
                <div className="bg-blue-light/10 dark:bg-blue-dark/20 border border-blue-dark/20 dark:border-blue-dark/30 rounded-lg p-5">
                  <h4 className="text-base font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                    Getting Started
                  </h4>
                  <div className="space-y-3 text-sm text-text-primary dark:text-text-primaryDark">
                    <div className="flex items-start gap-2">
                      <span className="text-secondary font-semibold">1.</span>
                      <p>
                        <strong>Configure Auto-Create Stripe Subscriptions:</strong> Enable the toggle and select the Net New Deal Pipeline and Stage. When deals reach this stage, Stripe subscriptions will be automatically created.
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-secondary font-semibold">2.</span>
                      <p>
                        <strong>Configure Renewal Deals in HubSpot:</strong> Enable the toggle and select the Renewal Pipeline and Stage where renewal deals will be automatically created in HubSpot.
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-secondary font-semibold">3.</span>
                      <p>
                        <strong>Create Contract Objects in HubSpot:</strong> Enable this to automatically create custom contract objects in HubSpot that mirror your platform contracts (requires HubSpot Enterprise).
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-secondary font-semibold">4.</span>
                      <p>
                        <strong>Use AI Suggestions:</strong> On first-time setup, AI will analyze your HubSpot pipelines and suggest the best configuration. Click "Get AI Suggestions" to refresh recommendations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Auto-Create Stripe Subscriptions */}
                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-6 border border-divider dark:border-divider-dark">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="p-2.5 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark">
                      <CreditCard className="w-5 h-5 text-text-primary dark:text-text-primaryDark" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                        Auto-Create Stripe Subscriptions
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-text-secondary dark:text-text-secondaryDark">Trigger:</span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-secondary/10 dark:bg-secondary/20 text-secondary dark:text-secondary border border-secondary/20 dark:border-secondary/30 text-xs font-medium">
                          Net New Deal Stage
                        </span>
                        <span className="text-xs text-text-secondary dark:text-text-secondaryDark">in</span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-secondary/10 dark:bg-secondary/20 text-secondary dark:text-secondary border border-secondary/20 dark:border-secondary/30 text-xs font-medium">
                          Net New Deal Pipeline
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3.5 pl-1">
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        When a deal reaches the configured stage in the selected pipeline, this feature automatically creates a <strong className="font-semibold">Stripe subscription</strong> and automatically creates the associated <strong className="font-semibold">contract</strong>.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        The Stripe subscription includes customer information, pricing plan, billing cycle, and payment method from the deal.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        Contracts are automatically created when Stripe subscriptions are created - no separate setting needed. This enables automatic billing and subscription management for new customers, streamlining your revenue operations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Create Renewal Deals in HubSpot */}
                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-6 border border-divider dark:border-divider-dark">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="p-2.5 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark">
                      <Calendar className="w-5 h-5 text-text-primary dark:text-text-primaryDark" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                        Create Renewal Deals in HubSpot
                      </h3>
                      <span className="inline-block px-3 py-1 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-md text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                        Trigger: Deal End Date - 90 days
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3.5 pl-1">
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        This feature automatically creates a <strong className="font-semibold">renewal deal</strong> in HubSpot 90 days before a deal's end date, ensuring your sales team has visibility into upcoming renewals.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        The renewal deal is automatically created in the configured Renewal Pipeline and Stage in HubSpot, linked to the original deal with all relevant customer and revenue information.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        This helps your sales team proactively reach out to customers before deals expire, improving renewal rates and ensuring no renewals are missed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Create Contract Objects in HubSpot */}
                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-6 border border-divider dark:border-divider-dark">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="p-2.5 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark">
                      <RefreshCw className="w-5 h-5 text-text-primary dark:text-text-primaryDark" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                        Create Contract Objects in HubSpot
                      </h3>
                      <span className="inline-block px-3 py-1 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-md text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                        Requires: HubSpot Enterprise
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3.5 pl-1">
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        This feature automatically creates <strong className="font-semibold">custom contract objects</strong> in HubSpot that mirror your platform contracts with a 1-to-1 mapping, allowing you to track and manage contracts directly in your CRM.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        When contracts are created on the platform, corresponding custom objects are automatically created in HubSpot and kept in sync, reflecting all contract data in your CRM.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        <strong className="font-semibold">Note:</strong> This feature requires HubSpot Enterprise. Please upgrade your HubSpot plan to enable this feature.
                      </p>
                    </div>
                  </div>
                </div>

                {/* C-Suite Daily Report */}
                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-6 border border-divider dark:border-divider-dark">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="p-2.5 bg-background-default dark:bg-background-dark rounded-lg border border-divider dark:border-divider-dark">
                      <Mail className="w-5 h-5 text-text-primary dark:text-text-primaryDark" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                        C-Suite Daily Report
                      </h3>
                      <span className="inline-block px-3 py-1 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-md text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                        Delivery: Daily Morning Email
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3.5 pl-1">
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        Receive a daily morning email snapshot with your total <strong className="font-semibold">ARR</strong>, RevSync activity, and key revenue metrics from the platform.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        The email is sent to the account owner each morning, providing a comprehensive overview of your revenue operations and platform activity.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-secondary dark:bg-text-secondaryDark mt-2 flex-shrink-0" />
                      <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark">
                        This helps you stay informed about your revenue performance and RevSync automation activity without needing to log into the platform.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-blue-light/10 dark:bg-blue-dark/20 border border-blue-dark/20 dark:border-blue-dark/30 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-dark dark:text-blue-dark mb-2">
                    How It Works
                  </h4>
                  <div className="space-y-2 text-sm text-text-primary dark:text-text-primaryDark">
                    <p>
                      <strong>AI-Powered Setup:</strong> On first-time setup, AI analyzes your HubSpot pipelines and suggests the best configuration. You can accept suggestions or customize as needed.
                    </p>
                    <p>
                      <strong>Configuration Required:</strong> Each feature requires proper configuration (pipeline and stage selection) before it can be enabled. This ensures features work correctly with your HubSpot setup.
                    </p>
                    <p>
                      <strong>Automatic Execution:</strong> Once enabled, features run automatically in the background. They monitor for trigger events and execute automation actions without manual intervention.
                    </p>
                    <p>
                      <strong>Easy Management:</strong> You can toggle features on or off at any time. Disabling a feature stops all automated actions immediately. Use "Get AI Suggestions" to refresh recommendations anytime.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

    </>
  );
};

export default RevSyncAgentSettings;

